import { create } from "zustand";

/**
 * Small UI state of the game screen, shared between the game components and the panels of other modules.
 * Stable contract: `tradeTarget` / `setTradeTarget` (TradePanel reads it to preselect a trading partner).
 */

export type GameTab = "players" | "assets" | "trade" | "chat";

export const DESKTOP_QUERY = "(min-width: 1024px)";

function isDesktop(): boolean {
  return typeof window !== "undefined" && window.matchMedia?.(DESKTOP_QUERY).matches === true;
}

interface UiState {
  /** Tab of the desktop sidebar. */
  activeTab: GameTab;
  /** Panel shown as bottom sheet on phones, null when closed. */
  sheet: GameTab | null;
  /** Player id the trade panel should preselect as partner, null for none. */
  tradeTarget: string | null;
  /** Field whose card is open. */
  selectedField: number | null;
  /** Player whose properties are shown read-only. */
  inspectedPlayer: string | null;
  /** The mortgage decision dialog was closed without deciding. */
  mortgageDialogHidden: boolean;
  /** Highest chat message id the viewer has seen. */
  chatSeenId: number;
  /** The keyboard shortcut overview is open. */
  hotkeyHelp: boolean;

  setTradeTarget: (id: string | null) => void;
  /** Opens a panel: switches the sidebar tab on desktop, opens the bottom sheet on phones. */
  openTab: (tab: GameTab) => void;
  setActiveTab: (tab: GameTab) => void;
  closeSheet: () => void;
  /** Preselects `playerId` and opens the trade panel. */
  openTradeWith: (playerId: string) => void;
  selectField: (index: number | null) => void;
  inspectPlayer: (id: string | null) => void;
  setMortgageDialogHidden: (hidden: boolean) => void;
  markChatSeen: (id: number) => void;
  setHotkeyHelp: (open: boolean) => void;
}

export const useUiStore = create<UiState>((set) => ({
  activeTab: "players",
  sheet: null,
  tradeTarget: null,
  selectedField: null,
  inspectedPlayer: null,
  mortgageDialogHidden: false,
  chatSeenId: 0,
  hotkeyHelp: false,

  setTradeTarget: (id) => set({ tradeTarget: id }),
  openTab: (tab) => set({ activeTab: tab, sheet: isDesktop() ? null : tab }),
  setActiveTab: (tab) => set({ activeTab: tab }),
  closeSheet: () => set({ sheet: null }),
  openTradeWith: (playerId) =>
    set({ tradeTarget: playerId, activeTab: "trade", sheet: isDesktop() ? null : "trade", inspectedPlayer: null, selectedField: null }),
  selectField: (index) => set({ selectedField: index }),
  inspectPlayer: (id) => set({ inspectedPlayer: id }),
  setMortgageDialogHidden: (hidden) => set({ mortgageDialogHidden: hidden }),
  markChatSeen: (id) => set((s) => (id > s.chatSeenId ? { chatSeenId: id } : s)),
  setHotkeyHelp: (open) => set({ hotkeyHelp: open }),
}));
